import type { Theme, CSSObject } from '@mui/material/styles';

import { useMemo } from 'react';

import { styled } from '@mui/material/styles';
import { SettingsState } from '../settings';
import { stylesMode, varAlpha } from '../theme/styles';
import { bulletColor } from '../nav-section';

// ----------------------------------------------------------------------

export const StyledDivider = styled('span')(({ theme }) => ({
  width: 1,
  height: 10,
  flexShrink: 0,
  display: 'none',
  position: 'relative',
  alignItems: 'center',
  flexDirection: 'column',
  marginLeft: theme.spacing(2.5),
  marginRight: theme.spacing(2.5),
  backgroundColor: 'currentColor',
  color: theme.vars.palette.divider,
  '&::before, &::after': {
    top: -5,
    width: 3,
    height: 3,
    content: '""',
    flexShrink: 0,
    borderRadius: '50%',
    position: 'absolute',
    backgroundColor: 'currentColor',
  },
  '&::after': { bottom: -5, top: 'auto' },
}));

// ----------------------------------------------------------------------

/** Returns the same background color used by the left nav panel (for drawer/sidebar consistency). */
export function getNavBackgroundColor(theme: Theme, settings: SettingsState): string {
  const isDark = theme.palette.mode === 'dark';
  const { vars: { palette } } = theme;
  switch (settings.navColor) {
    case 'integrate':
      // Light: true white sidebar; page body often uses grey default (#F7F8FA).
      return isDark ? theme.palette.background.paper : palette.common.white;
    case 'apparent':
      return isDark ? palette.grey[800] : palette.common.white;
    case 'theme':
      return isDark ? palette.background.paper : palette.common.white;
    default:
      return isDark ? palette.background.default : palette.common.white;
  }
}

// ----------------------------------------------------------------------

export function useNavColorVars(
  theme: Theme,
  settings: SettingsState
): Record<'layout' | 'section', CSSObject> {
  const {
    vars: { palette },
  } = theme;

  const isDark = theme.palette.mode === 'dark';

  return useMemo(() => {
    switch (settings.navColor) {
      case 'integrate':
        return {
          layout: {
            '--layout-nav-bg': isDark ? theme.palette.background.paper : palette.common.white,
            '--layout-nav-horizontal-bg': isDark
              ? varAlpha(palette.background.defaultChannel, 0.8)
              : palette.common.white,
            '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.12),
            '--layout-nav-text-primary-color': palette.text.primary,
            '--layout-nav-text-secondary-color': palette.text.primary,
            '--layout-nav-text-disabled-color': palette.text.disabled,
            [stylesMode.dark]: {
              '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], 0.08),
              '--layout-nav-horizontal-bg': varAlpha(palette.background.defaultChannel, 0.96),
            },
          },
          section: {
            '--nav-item-color': palette.text.primary,
            '--nav-item-hover-bg': palette.action.hover,
            '--nav-item-caption-color': palette.text.disabled,
            '--nav-item-root-active-color': palette.primary.main,
            '--nav-item-root-active-color-on-dark': palette.primary.light,
            '--nav-item-root-active-bg': varAlpha(palette.primary.mainChannel, 0.08),
            '--nav-item-root-active-hover-bg': varAlpha(palette.primary.mainChannel, 0.16),
            '--nav-item-root-open-color': palette.text.primary,
            '--nav-item-root-open-bg': palette.action.hover,
            '--nav-item-sub-active-color': palette.text.primary,
            '--nav-item-sub-active-bg': palette.action.selected,
            '--nav-item-sub-open-color': palette.text.primary,
            '--nav-item-sub-open-bg': palette.action.hover,
            '--nav-icon-color': palette.text.primary,
            '--nav-icon-active-color': palette.primary.main,
            '--nav-icon-open-color': palette.text.primary,
            '--nav-subheader-color': palette.text.disabled,
            '--nav-subheader-hover-color': palette.text.primary,
            [stylesMode.dark]: {
              '--nav-item-color': palette.text.primary,
              '--nav-item-root-active-color': palette.primary.light,
              '--nav-item-root-open-color': palette.text.primary,
              '--nav-item-sub-active-color': palette.text.primary,
              '--nav-item-sub-open-color': palette.text.primary,
              '--nav-icon-color': palette.text.primary,
              '--nav-icon-active-color': palette.primary.light,
              '--nav-icon-open-color': palette.text.primary,
              '--nav-subheader-color': palette.text.disabled,
              '--nav-subheader-hover-color': palette.text.primary,
            },
          },
        };
      case 'apparent':
        return {
          layout: {
            '--layout-nav-bg': palette.grey[900],
            '--layout-nav-horizontal-bg': varAlpha(palette.grey['900Channel'], 0.96),
            '--layout-nav-border-color': 'transparent',
            '--layout-nav-text-primary-color': palette.common.white,
            '--layout-nav-text-secondary-color': palette.grey[500],
            '--layout-nav-text-disabled-color': palette.grey[600],
            [stylesMode.dark]: {
              '--layout-nav-bg': palette.grey[800],
              '--layout-nav-horizontal-bg': varAlpha(palette.grey['800Channel'], 0.8),
            },
          },
          section: {
            '--nav-item-caption-color': palette.grey[600],
            '--nav-subheader-color': palette.grey[600],
            '--nav-subheader-hover-color': palette.common.white,
            '--nav-item-color': palette.grey[500],
            '--nav-item-root-active-color': palette.primary.light,
            '--nav-item-root-open-color': palette.common.white,
            '--nav-item-hover-bg': palette.action.hover,
            '--nav-item-root-active-bg': varAlpha(palette.primary.mainChannel, 0.08),
            '--nav-item-root-active-hover-bg': varAlpha(palette.primary.mainChannel, 0.16),
            '--nav-item-sub-active-color': palette.common.white,
            '--nav-item-sub-active-bg': palette.action.selected,
            '--nav-item-sub-open-color': palette.common.white,
            '--nav-item-sub-open-bg': palette.action.hover,
            '--nav-bullet-light-color': bulletColor.dark,
            '--nav-icon-color': palette.grey[500],
            '--nav-icon-active-color': palette.primary.light,
            '--nav-icon-open-color': palette.common.white,
            ...(settings.navLayout === 'vertical' && {
              '--nav-item-sub-active-color': palette.common.white,
              '--nav-item-sub-open-color': palette.common.white,
            }),
            [stylesMode.dark]: {
              '--nav-item-color': palette.grey[400],
              '--nav-item-root-active-color': palette.primary.light,
              '--nav-item-root-open-color': palette.common.white,
              '--nav-item-sub-active-color': palette.common.white,
              '--nav-item-sub-open-color': palette.common.white,
              '--nav-subheader-color': palette.grey[400],
              '--nav-subheader-hover-color': palette.common.white,
              '--nav-icon-color': palette.grey[400],
              '--nav-icon-active-color': palette.primary.light,
              '--nav-icon-open-color': palette.common.white,
            },
          },
        };
      case 'theme':
        return {
          layout: {
            '--layout-nav-bg': isDark ? palette.background.paper : palette.common.white,
            '--layout-nav-horizontal-bg': isDark ? palette.background.paper : palette.common.white,
            '--layout-nav-border-color': varAlpha(palette.grey['500Channel'], isDark ? 0.08 : 0.12),
            '--layout-nav-text-primary-color': isDark ? palette.common.white : palette.text.primary,
            '--layout-nav-text-secondary-color': isDark ? '#e0f2f2' : palette.text.primary,
            '--layout-nav-text-disabled-color': isDark ? '#a0c8c8' : palette.text.disabled,
          },
          section: {
            '--nav-item-caption-color': isDark ? '#a0c8c8' : palette.text.disabled,
            '--nav-subheader-color': isDark ? '#a0c8c8' : palette.text.disabled,
            '--nav-subheader-hover-color': isDark ? palette.common.white : palette.text.primary,
            '--nav-item-color': isDark ? '#e0f2f2' : palette.text.primary,
            '--nav-item-root-active-color': isDark ? palette.common.white : palette.primary.main,
            '--nav-item-root-open-color': isDark ? palette.common.white : palette.text.primary,
            '--nav-bullet-light-color': isDark ? palette.common.white : palette.primary.main,
            '--nav-icon-color': isDark ? '#e0f2f2' : palette.text.primary,
            '--nav-icon-active-color': isDark ? palette.common.white : palette.primary.main,
            '--nav-icon-open-color': isDark ? palette.common.white : palette.text.primary,
            ...(settings.navLayout === 'vertical' && {
              '--nav-item-sub-active-color': isDark ? palette.common.white : palette.text.primary,
              '--nav-item-sub-open-color': isDark ? palette.common.white : palette.text.primary,
            }),
          },
        };
      default:
        throw new Error(`Invalid color: ${settings.navColor}`);
    }
  }, [
    isDark,
    theme.palette.background.paper,
    palette.background.default,
    palette.background.defaultChannel,
    palette.common.white,
    palette.grey,
    palette.primary.light,
    palette.primary.main,
    palette.primary.mainChannel,
    palette.text.disabled,
    palette.text.primary,
    palette.text.primary,
    palette.action.hover,
    palette.action.selected,
    settings.navColor,
    settings.navLayout,
  ]);
}
